import type { ParentType } from '@risksmart-app/domain/src/types/consts/index';
import type {
  FormFieldOption,
  UpdateFormFieldMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetFormConfigurationByParentTypeDocument,
  GetFormCustomisationDocument,
  GetFormFieldOptionsByParentTypeDocument,
  UpdateFormFieldDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

type UpdateFormFieldInput = {
  ParentType: string;
  FieldId: string;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  IsCustomField: boolean;
  Label?: string | null;
  AltLabel?: string;
  Description?: string | null;
  Options?: FormFieldOption[];
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  Required: boolean;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  Hidden: boolean;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  ReadOnly: boolean;
  DefaultValue?: string | null;
  Conditions?: unknown;
};

type OptionField =
  | { _tag: 'StringOption'; Value: string }
  | { _tag: 'AltValueOption'; Value: string; AltValue: string };

type UpdateFormFieldTRPCInputBase = {
  ParentType: ParentType;
  FieldId: string;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  Required: boolean;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  Hidden: boolean;
  // no-dd-sa:typescript-best-practices/boolean-prop-naming
  ReadOnly: boolean;
  DefaultValue?: string | null;
  Conditions?: unknown;
};

type UpdateFormFieldTRPCInput =
  | (UpdateFormFieldTRPCInputBase & {
      IsCustomField: true;
      Label: string;
      AltLabel?: string;
      Description?: string | null;
      Options: OptionField[];
    })
  | (UpdateFormFieldTRPCInputBase & {
      IsCustomField: false;
      Label?: string | null;
      Description?: string | null;
    });

/**
 * Converts FormFieldOption to the tRPC-compatible option format
 */
const convertOptionsForTRPC = (options: FormFieldOption[]): OptionField[] =>
  options.map((opt) =>
    opt._tag === 'AltValueOption'
      ? {
          _tag: 'AltValueOption' as const,
          Value: opt.Value,
          AltValue: opt.AltValue ?? '',
        }
      : {
          _tag: 'StringOption' as const,
          Value: opt.Value,
        }
  );

export const useUpdateFormField = createMutationHook({
  name: 'updateFormField',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.formConfiguration.updateFormField.mutationOptions({
      onSuccess,
    }),
  // Build properly typed input for tRPC
  mapInput: (variables: UpdateFormFieldInput): UpdateFormFieldTRPCInput => {
    const parentType = variables.ParentType as ParentType;

    return variables.IsCustomField
      ? {
          IsCustomField: true,
          ParentType: parentType,
          FieldId: variables.FieldId,
          Label: variables.Label ?? '',
          AltLabel: variables.AltLabel,
          Description: variables.Description,
          Options: convertOptionsForTRPC(variables.Options ?? []),
          Required: variables.Required,
          Hidden: variables.Hidden,
          ReadOnly: variables.ReadOnly,
          DefaultValue: variables.DefaultValue,
          Conditions: variables.Conditions,
        }
      : {
          IsCustomField: false,
          ParentType: parentType,
          FieldId: variables.FieldId,
          Label: variables.Label,
          Description: variables.Description,
          Required: variables.Required,
          Hidden: variables.Hidden,
          ReadOnly: variables.ReadOnly,
          DefaultValue: variables.DefaultValue,
          Conditions: variables.Conditions,
        };
  },
  // Invalidate BOTH form-configuration readers — see useInsertFormField for
  // the rationale. `getFormCustomisation` backs the live form render;
  // skipping it leaves an open form stale (RSP-5866).
  invalidate: (trpc) => [
    trpc.frontend.formConfiguration.getByParentTypes.queryKey(),
    trpc.frontend.formConfiguration.getFormCustomisation.queryKey(),
  ],
  mapResponse: (result: { Id: string }): UpdateFormFieldMutation => ({
    updateFormField: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: UpdateFormFieldDocument,
  graphqlVariables: (variables: UpdateFormFieldInput) => ({
    object: {
      ParentType: variables.ParentType,
      FieldId: variables.FieldId,
      IsCustomField: variables.IsCustomField,
      Label: variables.Label,
      AltLabel: variables.AltLabel,
      Description: variables.Description,
      Options: variables.Options ?? [],
      Required: variables.Required,
      Hidden: variables.Hidden,
      ReadOnly: variables.ReadOnly,
      DefaultValue: variables.DefaultValue,
      Conditions: variables.Conditions,
    },
  }),
  refetchQueries: [
    GetFormConfigurationByParentTypeDocument,
    GetFormFieldOptionsByParentTypeDocument,
    GetFormCustomisationDocument,
  ],
});
